import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  ArrowRight, Heart, Shield, ShieldAlert, Users, Compass, Eye, Sparkles,
  BookOpen, Moon, Mic, Wind, HandMetal, Anchor, Activity, Flower2,
  Leaf, Headphones, BedDouble, Music, Library, GraduationCap,
  FileText, ClipboardList, MessageCircle, CalendarHeart, Map,
  ChevronDown, Globe, Flag, Target, Crown,
} from "lucide-react";
import { FounderStory } from "@/components/FounderStory";
import { TrustSocialProof } from "@/components/TrustSocialProof";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";
import { CompanyInfo } from "@/components/CompanyInfo";
import { FoldedSection, SectionNavigator } from "@/components/content/SectionNavigator";
import founderImage from "@/assets/founder-jana.jpg";


export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Recovery Trauma™ | Trauma-Informed Recovery Platform" },
      {
        name: "description",
        content:
          "Recovery Trauma™ is a trauma-informed educational and self-help platform built from lived experience and supported by a global community of 380,000+ people.",
      },
      { property: "og:title", content: "About Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Trauma-informed educational platform built from lived experience and supported by a global recovery community.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/about" },
      { httpEquiv: "Cache-Control", content: "no-cache, no-store, must-revalidate" },
      { httpEquiv: "Pragma", content: "no-cache" },
      { httpEquiv: "Expires", content: "0" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/about" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About Recovery Trauma™",
          url: "https://recoverytrauma.com/about",
          description:
            "Recovery Trauma™ is a trauma-informed educational and self-help platform built from lived experience.",
          mainEntity: {
            "@type": "Organization",
            name: "Recovery Trauma™",
            url: "https://recoverytrauma.com",
            description:
              "Survivor-led, trauma-informed education and self-help platform supported by a global recovery community.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://recoverytrauma.com" },
            {
              "@type": "ListItem",
              position: 2,
              name: "About",
              item: "https://recoverytrauma.com/about",
            },
          ],
        }),
      },
    ],
  }),

  component: AboutPage,
});

const FEATURES: { label: string; icon: React.ElementType }[] = [
  { label: "Recovery Journal", icon: BookOpen },
  { label: "Dream Journal & Reflections", icon: Moon },
  { label: "Voice Journal", icon: Mic },
  { label: "Guided Breathing", icon: Wind },
  { label: "EFT Tapping", icon: HandMetal },
  { label: "Grounding Exercises", icon: Anchor },
  { label: "Nervous System Regulation", icon: Activity },
  { label: "Somatic Exercises", icon: Flower2 },
  { label: "Trauma-Informed Yoga", icon: Leaf },
  { label: "Guided Meditations", icon: Headphones },
  { label: "Sleep Support", icon: BedDouble },
  { label: "Sounds & Meditations", icon: Music },
  { label: "Recovery Library", icon: Library },
  { label: "Courses", icon: GraduationCap },
  { label: "Worksheets", icon: FileText },
  { label: "Workbooks", icon: ClipboardList },
  { label: "Community", icon: MessageCircle },
  { label: "Live Events", icon: CalendarHeart },
  { label: "Personalized Recovery Roadmaps", icon: Map },
];

const ABOUT_SECTIONS = [
  { id: "about-story", label: "Our story and founder" },
  { id: "about-approach", label: "Our recovery approach" },
  { id: "about-values", label: "Mission, values and dedication" },
  { id: "about-inside", label: "What's inside and company details" },
];


function AboutPage() {
  // Scroll to #meet-the-founder (or any hash target) after the page mounts —
  // the founder section renders below the fold, so the browser's native hash
  // jump can fire before it exists.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        const foldedParent = el.closest("details");
        if (foldedParent instanceof HTMLDetailsElement) foldedParent.open = true;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.clearInterval(timer);
      } else if (++tries > 20) {
        window.clearInterval(timer);
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-6 md:py-16">
      <AboutIntro />

      <div className="mt-6">
        <SectionNavigator links={ABOUT_SECTIONS} />
      </div>

      <div className="space-y-4">
        <FoldedSection id="about-story" icon={Heart} title="Our story and founder" defaultOpen>
          <WhyExistsSection />
          <StatsSection />
          <FounderStory />
        </FoldedSection>

        <FoldedSection id="about-approach" icon={Compass} title="Our recovery approach">
          <WhyTheseTools />
          <RecoveryAreas />
          <WhyBuiltSection />
          <NoteFromJanaExplained />
          <FromSurvivingSection />
        </FoldedSection>

        <FoldedSection id="about-values" icon={Sparkles} title="Mission, values and dedication">
          <MissionVisionValues />
          <ImportantDisclaimer />
        </FoldedSection>

        <FoldedSection id="about-inside" icon={Library} title="What's inside and company details">

      {/* What's inside — colorful slider */}
      <section className="mt-4">
        <h2 className="text-center font-display text-2xl text-foreground md:text-3xl">
          What's inside Recovery Trauma™
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-center text-sm text-muted-foreground">
          Swipe through the full toolkit — tap any tile to explore.
        </p>
        <Carousel
          opts={{ align: "start", loop: true }}
          className="relative mx-auto mt-6 w-full max-w-5xl px-2 sm:px-10"
        >
          <CarouselContent className="-ms-3">
            {FEATURES.map(({ label, icon: Icon }) => (
              <CarouselItem
                key={label}
                className="basis-1/2 ps-3 sm:basis-1/3 md:basis-1/4 lg:basis-1/5"
              >
                <div className="group flex h-full min-h-[120px] flex-col items-center justify-center gap-2 rounded-2xl border border-gold/30 bg-gradient-to-br from-cream/70 via-card to-cream/50 px-3 py-5 text-center shadow-sm transition hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-[var(--shadow-gold)]">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)] ring-1 ring-gold/40 transition group-hover:scale-105">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-xs font-semibold leading-tight text-foreground/85">
                    {label}
                  </p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden border-gold/40 bg-cream/80 text-[var(--gold-deep)] hover:bg-cream sm:flex" />
          <CarouselNext className="hidden border-gold/40 bg-cream/80 text-[var(--gold-deep)] hover:bg-cream sm:flex" />
          <CarouselSwipeHint storageKey="about-features" />
        </Carousel>
      </section>

      <section className="mt-16 grid gap-5 md:grid-cols-3">
        <Pillar
          icon={Heart}
          title="Compassion first"
          text="Every tool, prompt, and word is written with a trauma-informed lens. No shame, no pressure, no streaks."
        />
        <Pillar
          icon={Shield}
          title="Safe & private"
          text="Your journals and reflections are yours alone. We protect them like we'd want ours protected."
        />
        <Pillar
          icon={Users}
          title="In community"
          text="A global community of 380,000+ people walking this path alongside you — never alone again."
        />
      </section>

      <TrustSocialProof />

      {/* Governance & Team Roles */}
      <section className="mt-12">
        <div className="rounded-2xl border border-gold/20 bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
              <Shield className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-2xl text-foreground">Governance & Team Roles</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Leadership structure, qualifications, responsibilities, boundaries, and
                accountability framework for Recovery Trauma™ — supporting compliance,
                safeguarding, governance, audit readiness, and platform accountability.
              </p>
              <Link
                to="/governance"
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                View Governance & Team Roles
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Escalation Pathway */}
      <section className="mt-6">
        <div className="rounded-2xl border border-gold/20 bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-[var(--gold-deep)]">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-2xl text-foreground">Escalation Pathway</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                How flagged issues, risks and safeguarding concerns are reviewed at Recovery
                Trauma™ — with clear decision points and accountability at each stage.
              </p>
              <Link
                to="/escalation-pathway"
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                View Escalation Pathway
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company information */}
      <section className="mt-12">
        <div className="mx-auto max-w-md">
          <CompanyInfo />
        </div>
      </section>
        </FoldedSection>
      </div>
    </div>
  );
}

function Pillar({
  icon: Icon,
  title,
  text,
  paragraphs,
}: {
  icon: React.ElementType;
  title: string;
  text?: string;
  paragraphs?: string[];
}) {
  const [expanded, setExpanded] = useState(false);
  const items = paragraphs ?? (text ? [text] : []);
  const previewCount = title === "Our Values" ? 2 : 1;
  const previewItems = items.slice(0, previewCount);
  const remainingItems = items.slice(previewCount);
  const hasMore = remainingItems.length > 0;

  return (
    <div className="rounded-2xl border border-border/40 bg-card p-6 text-center shadow-sm">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
        <Icon className="h-5 w-5 text-foreground" />
      </div>
      <h3 className="font-display text-xl text-foreground">{title}</h3>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-muted-foreground">
        {previewItems.map((p) => (
          <p key={p}>{p}</p>
        ))}
        {expanded &&
          remainingItems.map((p) => (
            <p key={p}>{p}</p>
          ))}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 rounded"
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Read more"}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </div>
  );
}

function AboutIntro() {
  const [expanded, setExpanded] = useState(false);
  return (
    <header className="relative overflow-hidden rounded-[2rem] border border-gold/30 bg-gradient-to-br from-cream/70 via-card to-gold-light/25 px-6 py-10 shadow-[var(--shadow-soft)] sm:px-10 sm:py-14">
      <Flower2
        aria-hidden
        className="pointer-events-none absolute -end-8 -top-10 h-40 w-40 text-gold/12"
        strokeWidth={1}
      />
      <p className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-cream/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
        <Heart className="h-3 w-3" /> About Recovery Trauma™
      </p>

      <h1 className="mt-5 font-display text-[28px] leading-[1.15] text-foreground sm:text-4xl md:text-[44px]">
        Created from lived experience.
        <span className="block text-[var(--gold-deep)]">
          Built for life beyond survival mode.
        </span>
      </h1>

      <p className="mt-5 max-w-2xl text-[16px] leading-[1.8] text-foreground/80 sm:text-[17px]">
        Recovery Trauma™ is a survivor-created, trauma-informed wellbeing platform bringing
        together education, practical tools, guided reflection and community in one supportive space.
      </p>

      <div className="mt-5 max-w-2xl rounded-2xl border border-gold/30 bg-card/70 p-5 text-[15.5px] leading-[1.8] text-foreground/85">
        <p className="font-display text-lg text-foreground">It was created from a simple idea:</p>
        <p className="mt-2 italic">
          What if the things that can support trauma recovery were easier to understand, easier to find and available in one place?
        </p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["Survivor-Created", "Trauma-Informed", "Self-Paced", "Privacy-Focused"].map((chip) => (
          <span
            key={chip}
            className="rounded-full border border-gold/35 bg-card/70 px-3 py-1.5 text-xs font-medium text-foreground/80"
          >
            {chip}
          </span>
        ))}
      </div>

      {expanded && (
        <div className="mt-6 max-w-2xl animate-fade-in space-y-4 text-[15.5px] leading-[1.85] text-muted-foreground">
          <p>
            Recovery Trauma™ supports people living with CPTSD, trauma, anxiety, burnout and
            chronic stress.
          </p>
          <p>
            Every feature has been thoughtfully developed with a focus on safety, nervous system
            regulation, self-reflection and gentle daily support.
          </p>
          <p>
            Inside you'll find guided breathing, grounding techniques, somatic and EFT practices,
            reflective journaling, educational resources, a supportive community, live workshops
            and a therapist directory — all in one calm space.
          </p>
        </div>
      )}

      <div className="mt-7">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gold/40 bg-cream/70 px-5 py-2.5 text-sm font-medium text-[var(--gold-deep)] shadow-[var(--shadow-soft)] transition-all hover:-translate-y-0.5 hover:bg-gold/15 hover:shadow-[var(--shadow-gold)]"
        >
          {expanded ? "Show less" : "What's inside, in short"}
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-300 ${
              expanded ? "rotate-180" : ""
            }`}
            strokeWidth={2.25}
          />
        </button>
      </div>
    </header>
  );
}

function StatsSection() {
  const stats = [
    { icon: Users, value: "380,000+", label: "Community Followers", description: "A growing global community interested in understanding trauma, healing and recovery." },
    { icon: Heart, value: "8+", label: "Years of the Founder's Personal Therapy", description: "Recovery Trauma™ grew from years of lived experience, therapy, learning, reflection and personal recovery work." },
    { icon: Flag, value: "🇩🇪 → 🇬🇧", label: "Germany to the United Kingdom", description: "Founder Jana Burkhardt is originally from Germany and now lives in the United Kingdom." },
    { icon: Crown, value: "1", label: "Founder-Built Vision", description: "Created from the ground up. The concept, structure and original platform were created by Jana from her own vision of bringing trauma education and recovery resources together." },
    { icon: Globe, value: "🌍 Global", label: "Community & Reach", description: "Recovery Trauma™ connects people across countries, backgrounds and different stages of recovery." },
    { icon: Target, value: "1", label: "Mission", description: "Healing Beyond Survival Mode™ — helping people better understand themselves, discover supportive tools and explore what recovery can look like for them." },
  ];

  return (
    <section className="mt-12" aria-labelledby="stats-heading">
      <h2 id="stats-heading" className="text-center font-display text-2xl leading-tight text-foreground md:text-3xl">
        Recovery Trauma™ in Numbers
      </h2>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map(({ icon: Icon, value, label, description }) => (
          <div
            key={label}
            className="rounded-2xl border border-gold/25 bg-card/80 p-5 text-center shadow-sm"
          >
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
              <Icon className="h-5 w-5 text-foreground" />
            </div>
            <p className="mt-3 font-display text-2xl font-semibold leading-tight text-foreground">
              {value}
            </p>
            <p className="mt-1 text-sm font-semibold text-foreground/85">{label}</p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Why I Chose These Tools
   ───────────────────────────────────────────────────────────── */
const TOOL_QUESTIONS = [
  "Why did my body sometimes react before I could think?",
  "Why could a situation suddenly make me feel unsafe?",
  "Why did certain emotions feel overwhelming?",
  "Why was it sometimes difficult to even identify what I was feeling?",
  "Why did I repeat certain patterns even when I understood them intellectually?",
  "And perhaps most importantly: what could I actually do when those moments happened?",
] as const;

const TOOL_DISCOVERIES: { icon: React.ElementType; text: string }[] = [
  { icon: Anchor, text: "Some helped me feel grounded." },
  { icon: Heart, text: "Some helped me recognise and name emotions." },
  { icon: FileText, text: "Some helped me understand trauma responses." },
  { icon: Activity, text: "Some gave me ways to express experiences that were difficult to put into words." },
  { icon: Flower2, text: "Some helped me reconnect with my body." },
  { icon: Users, text: "Others helped me understand relationships, boundaries, attachment, identity and the relationship between the mind and nervous system." },
];

function WhyTheseTools() {
  return (
    <section className="mt-16" aria-labelledby="why-tools-heading">
      <div className="text-center">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <Sparkles className="h-3.5 w-3.5" /> Lived experience
        </p>
        <h2
          id="why-tools-heading"
          className="mt-4 font-display text-[26px] leading-tight text-foreground sm:text-3xl md:text-4xl"
        >
          💛 Why I Chose These Tools
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-[1.8] text-foreground/80">
          During my recovery, I wasn't only trying to understand what had happened to me.
          I was trying to understand what was happening inside me.
        </p>
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {TOOL_QUESTIONS.map((q) => (
          <div
            key={q}
            className="rounded-2xl border border-gold/25 bg-card/80 p-5 text-[15px] leading-relaxed text-foreground/85 shadow-sm"
          >
            <span className="font-display text-lg text-[var(--gold-deep)]">“</span> {q}
          </div>
        ))}
      </div>

      <p className="mx-auto mt-8 max-w-xl text-center font-display text-lg text-foreground sm:text-xl">
        Over time, I explored many different approaches.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {TOOL_DISCOVERIES.map(({ icon: Icon, text }) => (
          <div
            key={text}
            className="flex items-start gap-3 rounded-2xl border border-border/50 bg-gradient-to-br from-cream/50 via-card to-card p-5 shadow-sm"
          >
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
              <Icon className="h-4 w-4 text-foreground" />
            </span>
            <p className="text-[15px] leading-relaxed text-foreground/85">{text}</p>
          </div>
        ))}
      </div>

      <p className="mx-auto mt-8 max-w-2xl rounded-2xl border border-gold/35 bg-cream/50 p-6 text-center text-[15.5px] leading-[1.8] text-foreground/85">
        That is why Recovery Trauma™ doesn't present one method as “the answer.”
        Instead, it brings different trauma-informed concepts, reflective practices and practical tools together so people can explore what feels supportive for them.
      </p>

      <p className="mx-auto mt-6 max-w-xl text-center text-[15px] leading-relaxed text-muted-foreground">
        Take what supports you. Explore at your own pace. Leave what doesn't feel right for you.
      </p>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Four recovery areas
   ───────────────────────────────────────────────────────────── */
const AREAS: {
  emoji: string;
  title: string;
  text: string;
  icon: React.ElementType;
}[] = [
  {
    emoji: "🧠",
    title: "Understand",
    icon: GraduationCap,
    text: "Learn more about trauma, emotions, nervous-system responses, attachment, relationships and behavioural patterns. Sometimes understanding why something is happening can reduce the fear and confusion surrounding it.",
  },
  {
    emoji: "🌿",
    title: "Regulate",
    icon: Wind,
    text: "Explore practical exercises designed to support grounding, calming, reconnecting with the present and nervous-system regulation. These include breathing, grounding, somatic exercises, bilateral practices, mindfulness and other techniques.",
  },
  {
    emoji: "✍️",
    title: "Process",
    icon: BookOpen,
    text: "Create space for reflection and emotional exploration through journaling, guided prompts, inner-child work, creative exercises and other reflective approaches. Not everything can immediately be explained in words. Processing can take many different forms.",
  },
  {
    emoji: "💛",
    title: "Integrate",
    icon: Heart,
    text: "Recovery is about more than understanding trauma. Integration explores relationships, boundaries, identity, self-compassion, creativity, meaning, wellbeing and gradually building a life that is no longer organised entirely around survival.",
  },
];

function RecoveryAreas() {
  return (
    <section className="mt-16" aria-labelledby="areas-heading">
      <div className="text-center">
        <h2
          id="areas-heading"
          className="font-display text-[26px] leading-tight text-foreground sm:text-3xl md:text-4xl"
        >
          The Four Areas of Recovery
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-muted-foreground">
          Recovery Trauma™ is organised around four interconnected areas.
        </p>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {AREAS.map(({ emoji, title, text, icon: Icon }) => (
          <div
            key={title}
            className="group relative overflow-hidden rounded-[1.75rem] border border-gold/30 bg-gradient-to-br from-cream/70 via-card to-gold-light/20 p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[var(--gold-deep)] hover:shadow-[var(--shadow-gold)]"
          >
            <Icon
              aria-hidden
              className="pointer-events-none absolute -end-4 -top-4 h-24 w-24 text-gold/12"
              strokeWidth={1}
            />
            <div className="relative">
              <span className="text-2xl">{emoji}</span>
              <h3 className="mt-3 font-display text-xl text-foreground">{title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">{text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Why I Built Recovery Trauma™
   ───────────────────────────────────────────────────────────── */
function WhyBuiltSection() {
  const scattered = [
    "A technique from therapy.",
    "A journal prompt somewhere else.",
    "A grounding exercise I wanted to remember.",
    "Something I learned about the nervous system.",
    "A DBT skill.",
    "An explanation that suddenly helped something make sense.",
    "A creative exercise.",
    "A meditation.",
    "A book.",
    "A question that changed the way I looked at something.",
  ];

  return (
    <section className="mt-16" aria-labelledby="why-built-heading">
      <div className="relative overflow-hidden rounded-[2rem] border border-gold/30 bg-card p-7 shadow-sm sm:p-10">
        <Flower2
          aria-hidden
          className="pointer-events-none absolute -end-6 -top-6 h-32 w-32 text-gold/10"
          strokeWidth={1}
        />
        <h2
          id="why-built-heading"
          className="font-display text-[26px] leading-tight text-foreground sm:text-3xl md:text-4xl"
        >
          Why I Built Recovery Trauma™
        </h2>

        <div className="mt-5 space-y-4 text-[15.5px] leading-[1.8] text-foreground/85">
          <p>
            For years, the things I found useful were scattered everywhere.
          </p>
        </div>

        <div className="mt-5 grid gap-2 sm:grid-cols-2">
          {scattered.map((item) => (
            <div
              key={item}
              className="rounded-xl border border-gold/20 bg-cream/40 px-4 py-3 text-[14px] leading-relaxed text-foreground/80"
            >
              {item}
            </div>
          ))}
        </div>

        <div className="mt-6 space-y-4 text-[15.5px] leading-[1.8] text-foreground/85">
          <p className="rounded-2xl border border-gold/35 bg-cream/50 p-5 text-center font-display text-lg italic leading-relaxed text-foreground">
            “Why is all of this so difficult to find when you're already overwhelmed?”
          </p>
          <p>
            Eventually, that question became Recovery Trauma™.
          </p>
          <p>
            I created the original platform myself and began bringing together the tools, education, exercises, journals and experiences that reflected what I had searched for throughout my own healing journey.
          </p>
          <p>
            Recovery Trauma™ has continued to grow from there.
          </p>
          <ul className="space-y-2 text-[15px] leading-[1.8] text-foreground/85">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]" />
              It is not a replacement for therapy.
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]" />
              It is not a promise that one exercise will heal trauma.
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]" />
              And it is not a programme telling you how quickly you should recover.
            </li>
          </ul>
          <p>
            It is a place to understand, explore, practise, reflect and discover what may support you.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   A Note From Jana — explained
   ───────────────────────────────────────────────────────────── */
function NoteFromJanaExplained() {
  return (
    <section className="mt-16" aria-labelledby="note-heading">
      <div className="relative overflow-hidden rounded-[2rem] border border-gold/30 bg-card p-7 shadow-sm sm:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <Sparkles className="h-3 w-3 text-[var(--gold-deep)]" />
          A Note From Jana
        </span>
        <h2
          id="note-heading"
          className="mt-4 font-display text-[26px] leading-tight text-foreground sm:text-3xl"
        >
          ✨ A Note From Jana
        </h2>

        <div className="mt-5 space-y-4 text-[15.5px] leading-[1.8] text-foreground/85">
          <p>
            Throughout Recovery Trauma™, you may see sections labelled <strong className="font-semibold">“A Note From Jana.”</strong>
          </p>
          <p>
            These are there for a reason.
          </p>
          <p>
            I didn't want Recovery Trauma™ to feel like an anonymous collection of worksheets and exercises.
          </p>
          <p>
            When you see <strong className="font-semibold">A Note From Jana</strong>, I'm sharing something from my own lived experience — perhaps why I chose to include a particular exercise, something I personally learned during recovery, or why a particular subject matters to me.
          </p>
          <p className="rounded-2xl border border-border/60 bg-muted/40 p-4 text-[14.5px] text-muted-foreground">
            These notes are personal reflections. They are not therapy, medical advice or professional recommendations.
          </p>
          <p>
            Not every exercise will feel right for every person — and that's okay.
          </p>
        </div>

        <blockquote className="mt-6 rounded-2xl border-s-2 border-gold/60 bg-cream/50 py-5 ps-5 pe-4 font-display text-lg italic leading-relaxed text-foreground">
          There is no single correct way to heal.
        </blockquote>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   From Surviving to Healing Beyond Survival Mode™
   ───────────────────────────────────────────────────────────── */
function FromSurvivingSection() {
  const points = [
    "Beginning to understand yourself.",
    "Recognising patterns.",
    "Learning what safety feels like.",
    "Finding your voice.",
    "Setting boundaries.",
    "Building relationships differently.",
    "Rediscovering creativity.",
    "Understanding your needs.",
    "Feeling emotions without being completely consumed by them.",
    "And slowly creating a life that contains more than survival.",
  ];

  return (
    <section className="mt-16" aria-labelledby="surviving-heading">
      <div className="text-center">
        <h2
          id="surviving-heading"
          className="font-display text-[26px] leading-tight text-foreground sm:text-3xl md:text-4xl"
        >
          From Surviving to Healing Beyond Survival Mode™
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-[15.5px] leading-[1.8] text-foreground/80">
          Recovery can mean more than reducing symptoms.
        </p>
      </div>

      <div className="mt-6 grid gap-2 sm:grid-cols-2">
        {points.map((point) => (
          <div
            key={point}
            className="flex items-start gap-3 rounded-2xl border border-gold/20 bg-card/80 px-4 py-3 text-[14.5px] leading-relaxed text-foreground/85"
          >
            <Heart className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
            {point}
          </div>
        ))}
      </div>

      <p className="mx-auto mt-6 max-w-2xl text-center text-[15.5px] leading-[1.8] text-foreground/80">
        Healing doesn't have to happen all at once. You can learn, explore and move forward at your own pace.
      </p>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Personal Dedication
   ───────────────────────────────────────────────────────────── */
function DedicationSection() {
  return (
    <section className="mt-16" aria-labelledby="dedication-heading">
      <div className="relative mx-auto max-w-2xl overflow-hidden rounded-[2rem] border border-gold/30 bg-gradient-to-br from-cream/70 via-card to-gold-light/30 p-8 shadow-[var(--shadow-gold)] backdrop-blur-sm sm:p-12">
        <Flower2
          aria-hidden
          className="pointer-events-none absolute -end-6 -top-6 h-32 w-32 text-gold/15"
          strokeWidth={1}
        />
        <Flower2
          aria-hidden
          className="pointer-events-none absolute -bottom-8 -start-8 h-32 w-32 text-gold/10"
          strokeWidth={1}
        />

        <div className="relative text-center">
          <span className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-cream/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            <Heart className="h-3 w-3" /> In Loving Memory
          </span>
          <h2
            id="dedication-heading"
            className="mt-5 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            💛 A Personal Dedication
          </h2>
        </div>

        <div className="relative mt-8 space-y-5 text-[16px] leading-[1.9] text-foreground/85 sm:text-[17px]">
          <p>
            Recovery Trauma™ was created from lived experience, resilience and the belief that life beyond survival mode is possible.
          </p>
          <p>
            There are two people whose place in my story I want to acknowledge.
          </p>
          <p>
            First, my dad. He has been there for me in ways that are difficult to put into words — patient when I was learning to understand myself, steady when things felt impossible, and generous with his belief in me long before I believed in myself.
          </p>
          <p>
            Throughout the development of Recovery Trauma™, he supported me, encouraged me, guided me and reminded me to keep moving forward, even on the days when moving forward felt hardest.
          </p>
          <p>
            Dad, thank you. Your support became an important part of bringing this vision to life, and I could not have built it without you.
          </p>
          <p>
            I would also like to dedicate Recovery Trauma™ to my mother.
          </p>
        </div>

        <figure className="relative mx-auto mt-8 max-w-md rounded-2xl border border-gold/40 bg-card/80 px-6 py-7 text-center shadow-sm backdrop-blur">
          <Heart
            aria-hidden
            className="mx-auto h-6 w-6 text-[var(--gold-deep)]"
            strokeWidth={1.5}
          />
          <figcaption className="mt-3 font-display text-2xl leading-tight text-foreground sm:text-3xl">
            Lena Burkhardt
          </figcaption>
          <div className="mx-auto mt-4 h-px w-12 bg-gold/40" />
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            8 March 1965 — 24 December 1996
          </p>
        </figure>

        <div className="relative mt-8 space-y-5 text-[16px] leading-[1.9] text-foreground/85 sm:text-[17px]">
          <p>
            Losing my mother changed my life forever.
          </p>
          <p>
            That loss became part of a much longer journey of trying to understand trauma, grief, emotional pain, survival and eventually recovery.
          </p>
          <p>
            Every lesson, every challenge and every step of my own healing journey contributed in some way to the creation of Recovery Trauma™.
          </p>
          <p className="text-center font-display text-lg text-[var(--gold-deep)] sm:text-xl">
            Recovery Trauma™ is dedicated to you.
          </p>
          <p>
            May something created from my own journey help another person feel understood, find support or discover hope within theirs.
          </p>
        </div>

        <div className="relative mt-8 border-t border-gold/30 pt-6 text-center">
          <p className="text-sm italic text-muted-foreground">With love,</p>
          <p className="mt-2 font-display text-xl text-foreground">
            Jana Burkhardt
          </p>
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            Founder &amp; CEO · Recovery Trauma™
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   With Gratitude — personal acknowledgements
   ───────────────────────────────────────────────────────────── */
function GratitudeSection() {
  return (
    <section className="mx-auto mt-16 max-w-2xl">
      <div className="text-center">
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          <Heart className="h-3.5 w-3.5" /> With Gratitude
        </p>
        <h2 className="mt-4 font-display text-3xl leading-tight text-foreground md:text-4xl">
          A Personal Acknowledgement
        </h2>
      </div>

      <div className="mt-8 space-y-5 text-[16px] leading-[1.9] text-foreground/85 sm:text-[17px]">
        <p>
          Recovery Trauma™ would not exist without the people who supported me throughout my own healing journey.
        </p>
        <p>
          I would like to express my sincere gratitude to{" "}
          <strong className="font-semibold text-foreground">
            Nanneke Morgan
          </strong>
          , who was my therapist, for the support, compassion, insight and therapeutic guidance she provided during some of the most challenging periods of my life.
        </p>
        <p>
          Eight years of therapy taught me many things, but perhaps one of the most important was that healing rarely happens completely alone.
        </p>
        <p>
          Sometimes it is supported by people who create a safe space.
        </p>
        <p>
          People who listen.
        </p>
        <p>
          People who ask difficult questions with compassion.
        </p>
        <p>
          People who help us see something differently.
        </p>
        <p>
          And people who help us reconnect with ourselves when we feel lost.
        </p>
        <p>
          Thank you for being part of my journey and for the work you continue to do supporting others.
        </p>
        <p className="pt-2 text-muted-foreground">
          With gratitude,
          <br />
          <span className="font-display text-lg text-foreground">
            Jana Burkhardt
          </span>
          <br />
          <span className="text-sm">Founder, Recovery Trauma™</span>
        </p>
      </div>

      {/* Therapist profile card */}
      <div className="mt-10 rounded-2xl border border-gold/25 bg-card p-6 shadow-sm sm:p-7">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          In acknowledgement
        </p>
        <h3 className="mt-2 font-display text-xl text-foreground sm:text-2xl">
          Nanneke Morgan
        </h3>
        <p className="mt-1 text-sm text-[var(--gold-deep)]">
          My Therapist · Trauma Therapist &amp; Counsellor
        </p>
        <p className="mt-4 text-sm text-muted-foreground">
          With heartfelt thanks for eight years of guidance and support.
        </p>
      </div>

      <p className="mt-6 rounded-xl border border-border/60 bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
        This acknowledgement reflects Jana Burkhardt's individual lived experience and should not be interpreted as a professional endorsement or recommendation. Individuals are encouraged to undertake their own research when choosing a therapist or mental-health professional.
      </p>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Mission / Vision / Values
   ───────────────────────────────────────────────────────────── */
function MissionVisionValues() {
  return (
    <section className="mt-12 grid items-start gap-5 md:grid-cols-3">
      <Pillar
        icon={Compass}
        title="Our Mission"
        paragraphs={[
          "To help people affected by trauma understand what they are experiencing, discover practical tools and feel less alone in their recovery.",
          "Recovery Trauma™ brings together trauma education, guided exercises, lived experience, reflection and community support so people can explore recovery at their own pace.",
          "We want support to feel accessible rather than overwhelming.",
        ]}
      />
      <Pillar
        icon={Eye}
        title="Our Vision"
        paragraphs={[
          "A world where life after trauma can become more than survival.",
          "We want people affected by trauma to feel understood, supported and empowered to reconnect with themselves and build lives that aren't defined solely by what happened to them.",
          "Our vision is for trauma-informed knowledge and practical recovery resources to become easier to find, easier to understand and more accessible to the people searching for them.",
        ]}
      />
      <Pillar
        icon={Sparkles}
        title="Our Values"
        paragraphs={[
          "💛 Lived Experience Matters — Recovery Trauma™ was born from lived experience. We believe survivor voices deserve to be part of conversations about trauma and recovery.",
          "🌿 There Is No Single Path — What helps one person may not help another. Recovery is individual, and people should have space to explore different approaches.",
          "🤍 Compassion Without Judgement — Trauma can affect emotions, relationships, behaviour, identity and the body in complicated ways. People deserve understanding rather than shame.",
          "🧠 Education Should Empower — Understanding trauma can help people make sense of experiences that once felt confusing or frightening.",
          "🔒 Privacy Matters — People should be able to explore sensitive subjects with dignity and privacy.",
          "🫶 Recovery Is Not a Race — We do not believe healing should be measured by how quickly someone appears to be “better.” You are allowed to have difficult days, change direction, discover that something doesn't work for you, and move at your own pace.",
        ]}
      />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Why Recovery Trauma™ Exists
   ───────────────────────────────────────────────────────────── */
function WhyExistsSection() {
  return (
    <section className="mt-16" aria-labelledby="why-exists-heading">
      <div className="relative overflow-hidden rounded-[2rem] border border-gold/30 bg-gradient-to-br from-cream/70 via-card to-gold-light/25 p-8 shadow-sm sm:p-12">
        <Flower2
          aria-hidden
          className="pointer-events-none absolute -end-8 -top-10 h-40 w-40 text-gold/12"
          strokeWidth={1}
        />
        <div className="text-center">
          <h2
            id="why-exists-heading"
            className="font-display text-[26px] leading-tight text-foreground sm:text-3xl md:text-4xl"
          >
            Why Recovery Trauma™ Exists
          </h2>
        </div>

        <div className="mt-6 space-y-4 text-[16px] leading-[1.85] text-foreground/85 sm:text-[17px]">
          <p>
            Because sometimes you need to understand what is happening.
          </p>
          <p>
            Sometimes you need something that helps you regulate.
          </p>
          <p>
            Sometimes you need space to process.
          </p>
          <p>
            Sometimes you're ready to rebuild and integrate.
          </p>
          <p>
            And sometimes you simply need to know:
          </p>
          <p className="text-center font-display text-xl text-[var(--gold-deep)] sm:text-2xl">
            I'm not the only person who has felt like this.
          </p>
          <p>
            Recovery Trauma™ exists to create a place for all of those moments.
          </p>
        </div>

        <div className="mt-6 text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/70 px-4 py-2 text-sm font-medium text-[var(--gold-deep)]">
            <span>Understand</span>
            <span className="text-foreground/40">·</span>
            <span>Regulate</span>
            <span className="text-foreground/40">·</span>
            <span>Process</span>
            <span className="text-foreground/40">·</span>
            <span>Integrate</span>
          </p>
          <p className="mt-3 font-display text-lg text-foreground">
            Healing Beyond Survival Mode™
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   Important disclaimer
   ───────────────────────────────────────────────────────────── */
function ImportantDisclaimer() {
  return (
    <section className="mt-12" aria-labelledby="important-heading">
      <div className="rounded-2xl border border-gold/30 bg-card p-6 shadow-sm sm:p-8">
        <h2
          id="important-heading"
          className="font-display text-xl text-foreground sm:text-2xl"
        >
          Important
        </h2>
        <div className="mt-4 space-y-4 text-[15px] leading-[1.8] text-foreground/85">
          <p>
            Recovery Trauma™ is an educational and wellbeing platform and is not a substitute for therapy, medical care, diagnosis or treatment.
          </p>
          <p>
            The founder's lived experience is shared to provide personal context behind the creation of the platform and should not be interpreted as clinical or professional mental-health advice.
          </p>
        </div>
      </div>
    </section>
  );
}
